import {
	Button,
	type ColumnDef,
	DataTable,
	FilterBuilder,
	type FilterField,
	SearchInput,
	TintedBackgroundIcon,
	useFilterBuilder,
	useUrlPagination,
} from "@monbits/ui";
import { Coins, FlaskConical, RotateCw, TriangleAlert } from "lucide-react";
import * as React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import CopyButton from "src/components/copy-button/copy-button";
import { CryptoIcon } from "src/components/crypto-icon/crypto-icon";
import { StatusBadge } from "src/components/status-badge";
import paymentProvider, {
	type AdminPayment,
	type PaginatedPaymentsResponse,
} from "src/providers/payment-provider";
import { formatCryptoAmount } from "src/utils/format";
import { fiatOptions as CURRENCY_OPTIONS } from "src/utils/options";
import { getStatusBadgeProps, STATUS_CONFIG } from "src/utils/status-config";
import { formatDateTime, formatFiat } from "./format";

// Payment status values to show in filter. "expired" is an admin-derived view
// of timed-out failures (see ListPaymentsForAdmin); "failed" covers the rest.
const PAYMENT_STATUSES = [
	"pending",
	"inProgress",
	"success",
	"failed",
	"expired",
	"aml_rejected",
] as const;

// Generate status options from unified config
const PAYMENT_STATUS_OPTIONS = PAYMENT_STATUSES.map((status) => {
	const config = STATUS_CONFIG[status];
	return {
		value: status,
		label: config.label,
		icon: (
			<TintedBackgroundIcon icon={config.icon} color={config.color} size="sm" />
		),
	};
});

const MODE_OPTIONS = [
	{ value: "false", label: "Live", color: "#22c55e" },
	{ value: "true", label: "Test", color: "#3b82f6" },
];

const FILTER_FIELDS: FilterField[] = [
	{
		id: "status",
		label: "Status",
		type: "select",
		icon: <STATUS_CONFIG.pending.icon className="h-4 w-4" />,
		options: PAYMENT_STATUS_OPTIONS,
	},
	{
		id: "currency",
		label: "Currency",
		type: "select",
		icon: <Coins className="h-4 w-4" />,
		options: CURRENCY_OPTIONS,
	},
	{
		id: "isTest",
		label: "Mode",
		type: "select",
		icon: <FlaskConical className="h-4 w-4" />,
		options: MODE_OPTIONS,
	},
];

const columns: ColumnDef<AdminPayment>[] = [
	{
		id: "merchant",
		header: "Merchant",
		cell: (row) => (
			<>
				<span className="font-medium">{row.merchantName}</span>
				{row.isTest && (
					<StatusBadge
						type="info"
						className="ml-2 text-[10px] uppercase tracking-wide"
					>
						Test
					</StatusBadge>
				)}
			</>
		),
	},
	{
		id: "status",
		header: "Status",
		cell: (row) => {
			const config = getStatusBadgeProps(row.status);
			return (
				<StatusBadge type={config.type} icon={config.icon}>
					{config.label}
				</StatusBadge>
			);
		},
	},
	{
		id: "currency",
		header: "Paid in",
		cell: (row) =>
			row.latestTransaction?.currency ? (
				<CryptoIcon
					ticker={row.latestTransaction.currency}
					showLabel
					showComposite
					className="h-5 w-5"
				/>
			) : (
				<span className="text-muted-foreground">—</span>
			),
	},
	{
		id: "amount",
		header: "Amount",
		className: "text-right",
		cell: (row) => {
			const tx = row.latestTransaction;
			// Only a payment that actually succeeded counts as "paid". Failed /
			// expired payments may still have on-chain receipts, but surfacing
			// them as "paid" is misleading.
			const isPaid = row.status === "success" || row.status === "inProgress";
			const received =
				isPaid && tx?.currency ? (tx.factAmount ?? tx.amount) : null;
			const token = tx?.currency?.split("_").pop();
			return (
				<div className="flex flex-col items-end leading-tight">
					<span className="font-mono tabular-nums whitespace-nowrap font-medium">
						{formatFiat(row.price, row.currency)}
					</span>
					{received && tx?.currency && (
						<span className="font-mono text-xs text-muted-foreground tabular-nums whitespace-nowrap">
							{formatCryptoAmount(received, { ticker: tx.currency })} {token}{" "}
							paid
						</span>
					)}
				</div>
			);
		},
	},
	{
		id: "created",
		header: "Created",
		cell: (row) => (
			<span className="whitespace-nowrap">{formatDateTime(row.createdAt)}</span>
		),
	},
	{
		id: "uuid",
		header: "Payment ID",
		cell: (row) => (
			<span className="flex items-center gap-1">
				<span className="font-mono text-xs text-muted-foreground">
					{row.publicId.slice(0, 8)}
				</span>
				<CopyButton value={row.publicId} label="Payment public ID" />
			</span>
		),
	},
];

export function PaymentsList() {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();

	const [payments, setPayments] = React.useState<AdminPayment[]>([]);
	const [totalCount, setTotalCount] = React.useState(0);
	const [isLoading, setIsLoading] = React.useState(true);
	const [hasError, setHasError] = React.useState(false);

	const { filters, setFilters } = useFilterBuilder({
		fields: FILTER_FIELDS,
		searchParams,
		setSearchParams,
	});

	const search = searchParams.get("search") || "";

	const { page, limit, offset, setPage, setLimit } = useUrlPagination({
		totalCount,
	});

	// Convert filter builder filters to API params
	const apiFilters = React.useMemo(() => {
		const result: {
			status?: string;
			currency?: string;
			isTest?: boolean;
		} = {};

		for (const f of filters) {
			if (f.operator === "is") {
				if (f.field === "status") {
					result.status = f.value;
				} else if (f.field === "currency") {
					result.currency = f.value;
				} else if (f.field === "isTest") {
					result.isTest = f.value === "true";
				}
			}
		}

		return result;
	}, [filters]);

	const loadPayments = React.useCallback(async () => {
		try {
			setIsLoading(true);
			setHasError(false);

			const response: PaginatedPaymentsResponse =
				await paymentProvider.listPayments({
					limit,
					offset,
					search,
					status: apiFilters.status,
					currency: apiFilters.currency,
					isTest: apiFilters.isTest,
				});

			setPayments(response.payments);
			setTotalCount(response.totalCount);
		} catch (error) {
			console.error("Failed to load payments:", error);
			setHasError(true);
			setPayments([]);
			setTotalCount(0);
			toast.error("Could not load payments", {
				description: "The request failed. Check your connection and try again.",
			});
		} finally {
			setIsLoading(false);
		}
	}, [offset, limit, search, apiFilters]);

	React.useEffect(() => {
		loadPayments();
	}, [loadPayments]);

	const handleViewPayment = React.useCallback(
		(payment: AdminPayment) => {
			navigate(`/payments/${payment.publicId}`);
		},
		[navigate],
	);

	return (
		<div className="p-4 sm:p-6 space-y-6">
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-3xl font-bold">Payment Management</h1>
					<p className="text-sm text-muted-foreground mt-1">
						{totalCount} total payment{totalCount !== 1 ? "s" : ""}
					</p>
				</div>
			</div>

			<div className="flex gap-4 flex-wrap items-center mb-4">
				<SearchInput
					placeholder="Search by merchant, payment ID, or customer..."
					paramName="search"
					className="w-[350px]"
				/>
				<FilterBuilder
					fields={FILTER_FIELDS}
					filters={filters}
					onFiltersChange={setFilters}
				/>
			</div>

			<DataTable
				columns={columns}
				data={payments}
				isLoading={isLoading}
				onRowClick={handleViewPayment}
				getRowId={(row) => String(row.id)}
				emptyIcon={
					hasError ? (
						<TriangleAlert className="size-6 text-destructive mb-3" />
					) : undefined
				}
				emptyMessage={
					hasError
						? "Couldn't load payments"
						: search || filters.length > 0
							? "No payments found. Try adjusting your search or filter criteria."
							: "No payments yet. Payments will appear here once they are created."
				}
				emptyDescription={
					hasError ? "Something went wrong while fetching payments." : undefined
				}
				emptyAction={
					hasError ? (
						<Button variant="outline" size="sm" onClick={() => loadPayments()}>
							<RotateCw className="size-4" />
							Retry
						</Button>
					) : undefined
				}
				pagination={{
					page,
					pageSize: limit,
					totalCount,
					onPageChange: setPage,
					onPageSizeChange: setLimit,
				}}
			/>
		</div>
	);
}
